// This file is part of Invenio-RDM-Records
// Copyright (C) 2020-2024 CERN.
// Copyright (C) 2020-2022 Northwestern University.
// Copyright (C) 2021 Graz University of Technology.
//
// Invenio-RDM-Records is free software; you can redistribute it and/or modify it
// under the terms of the MIT License; see LICENSE file for more details.

import React, { Component } from "react";
import PropTypes from "prop-types";
import { GroupField, SelectField, TextField } from "react-invenio-forms";
import { Button, Form, Icon } from "semantic-ui-react";
import _isEmpty from "lodash/isEmpty";
import _matches from "lodash/matches";
import _filter from "lodash/filter";
import _isEqual from "lodash/isEqual";
import _has from "lodash/has";
import { emptyDate } from "./initialValues";
import { i18next } from "@translations/invenio_rdm_records/i18next";
import { sortOptions } from "../../utils";
import Overridable from "react-overridable";
import { ArrayField } from "../Overrides";

export class TemporalCoverageField extends Component {
  /** Top-level Dates Component */

  /**
   * Returns the required option if the current value passed does match it
   * @param  {Object} currentValue The current value
   * @param  {Array} arrayOfValues The array of values for the field
   * @return {Object} The required option if any
   */
  getRequiredOption = (currentValue, arrayOfValues) => {
    const { requiredOptions } = this.props;
    for (const requiredOption of requiredOptions) {
      // If more values matched we do take the first value
      const matchingValue = _filter(arrayOfValues, _matches(requiredOption))[0];
      if (_isEqual(matchingValue, currentValue)) {
        return requiredOption;
      }
    }
    return null;
  };

  render() {
    const {
      fieldPath,
      label,
      labelIcon,
      placeholderStartDate,
      placeholderEndDate,
      required,
      requiredOptions,
      showEmptyValue,
      options,
    } = this.props;

    return (
      <Overridable
        id="InvenioRdmRecords.TemporalCoverageField.AddDateArrayField.Container"
        fieldPath={fieldPath}
      >
        <ArrayField
          addButtonLabel={i18next.t("Add Temporal Coverage")}
          defaultNewValue={emptyDate}
          fieldPath={fieldPath}
          helpText={i18next.t(
            "The time period covered by the content of this resource. Use this to describe when the data, observations, or subject matter apply, not when the resource was created or published. Format: DATE or DATE/DATE where DATE is YYYY or YYYY-MM or YYYY-MM-DD."
          )}
          label={label}
          labelIcon={labelIcon}
          required={required}
          requiredOptions={requiredOptions}
          showEmptyValue={showEmptyValue}
        >
          {({ array, arrayHelpers, indexPath, value }) => {
            const fieldPathPrefix = `${fieldPath}.${indexPath}`;
            const requiredOption = this.getRequiredOption(value, array);
            const hasRequiredStartDateValue = _has(requiredOption, "start_date");
            const hasRequiredEndDateValue = _has(requiredOption, "end_date");
            const selectedOption = value?.type || "";

            return (
              <GroupField fieldPath={fieldPath} optimized>
                {/* Dropdown Menu (TODO)
                This section doesn't work right now. The dropdown menu list the possible types (spans or ranges) but we cannot click it.
                THe mechanism is rendering correctly the already present fields and the api works as expected. This is a javascript issue.
                */}
                <SelectField
                  fieldPath={`${fieldPathPrefix}.type`}
                  label={i18next.t("Type")}
                  aria-label={i18next.t("Type")}
                  options={[
                    { text: "Range", value: "ranges" },
                    { text: "Span", value: "spans" },
                  ]}
                  width={5}
                  optimized
                />
                {selectedOption === "ranges" && (
                  <>
                    <Overridable
                      id="InvenioRdmRecords.TemporalCoverageField.DateTextField.Container"
                      fieldPath={`${fieldPathPrefix}.ranges.start_date`}
                    >
                      <TextField
                        fieldPath={`${fieldPathPrefix}.ranges.start_date`}
                        label={i18next.t("Start Date")}
                        placeholder={placeholderStartDate}
                        disabled={hasRequiredStartDateValue}
                        width={5}
                      />
                    </Overridable>
                    <Overridable
                      id="InvenioRdmRecords.TemporalCoverageField.DateTextField.Container"
                      fieldPath={`${fieldPathPrefix}.ranges.end_date`}
                    >
                      <TextField
                        fieldPath={`${fieldPathPrefix}.ranges.end_date`}
                        label={i18next.t("End Date")}
                        placeholder={placeholderEndDate}
                        disabled={hasRequiredEndDateValue}
                        width={5}
                      />
                    </Overridable>
                  </>
                )}

                {/* Conditionally Render "Span" Field */}
                {selectedOption === "spans" && (
                  <Overridable
                    id="InvenioRdmRecords.TemporalCoverageField.SpanTextField.Container"
                    fieldPath={`${fieldPathPrefix}.spans`}
                  >
                    <TextField
                      fieldPath={`${fieldPathPrefix}.spans`}
                      label={i18next.t("Span")}
                      placeholder="Enter time span"
                      required
                      width={10}
                    />
                  </Overridable>
                )}
                <Overridable id="InvenioRdmRecords.TemporalCoverageField.RemoveFormField.Container">
                  <Form.Field>
                    <Button
                      aria-label={i18next.t("Remove field")}
                      className="close-btn"
                      disabled={!_isEmpty(requiredOption)}
                      icon
                      onClick={() => arrayHelpers.remove(indexPath)}
                      type="button"
                    >
                      <Icon name="close" />
                    </Button>
                  </Form.Field>
                </Overridable>
              </GroupField>
            );
          }}
        </ArrayField>
      </Overridable>
    );
  }
}

TemporalCoverageField.propTypes = {
  fieldPath: PropTypes.string.isRequired,
  label: PropTypes.string,
  labelIcon: PropTypes.string,
  required: PropTypes.bool,
  placeholderStartDate: PropTypes.string,
  placeholderEndDate: PropTypes.string,
  requiredOptions: PropTypes.array,
  showEmptyValue: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.string,
    })
  ).isRequired,
};

TemporalCoverageField.defaultProps = {
  label: i18next.t("Temporal Coverage"),
  labelIcon: "calendar",
  placeholderStartDate: i18next.t("YYYY-MM-DD or YYYY-MM-DD/YYYY-MM-DD"),
  placeholderEndDate: i18next.t("YYYY-MM-DD or YYYY-MM-DD/YYYY-MM-DD"),
  required: false,
  requiredOptions: [],
  showEmptyValue: false,
};
